import { apiClient, getRequestHeaders, IBaseResponse, extractPaginationInfo } from './apiBase';
import { AxiosResponse } from 'axios';

import { IRepositoryFile, IPagination, IRepositoryFileRequest, TEntryDataType } from '@/types';
import { BASE_API_URL } from '@/config/environment';
import * as Sentry from '@sentry/nextjs';

export interface IRepositoryFileResponse extends IBaseResponse {
  file?: IRepositoryFile;
  pagination?: IPagination;
}

const responseHandler = (
  res: AxiosResponse,
  fileInfo: IRepositoryFileRequest,
  url: string,
  imgSrc?: string,
): IRepositoryFileResponse => {
  const { resource, dataType, mimeType } = fileInfo;
  const { status, status_message, error } = res.data;
  const file: IRepositoryFile = {
    filename: resource,
    data_type: dataType as TEntryDataType,
    mime_type: mimeType as string,
    data: res.data,
    imgSrc,
    url: url,
  };
  const pagination = extractPaginationInfo(res.data);
  return {
    status,
    status_message,
    file,
    pagination,
    error: error || null,
  };
};

export const getFileContent = async ({
  fileInfo,
  query,
  token,
  emitProgress,
}: {
  fileInfo: IRepositoryFileRequest;
  query?: URLSearchParams;
  token?: string | undefined;
  emitProgress?: (progress: TDownloadProgress) => void;
}): Promise<IRepositoryFileResponse | null> => {
  switch (fileInfo.dataType) {
    case 'tabular':
      throw new Error('Tabular Files are not supported by the repositoryFileApi, use the dataFrameApi instead');
    default:
      return getRepositoryFileContent({ fileInfo, token, emitProgress });
  }
};

const getRepositoryFilePath = (fileInfo: IRepositoryFileRequest): string => {
  const { namespace, repositoryName, resource } = fileInfo;
  return `/repos/${namespace}/${repositoryName}/file/${resource}`;
};

export type TDownloadProgress = {
  loaded: number;
  total: number;
  percentCompleted: number;
};

const getRepositoryFileContent = async ({
  fileInfo,
  token,
  emitProgress,
}: {
  fileInfo: IRepositoryFileRequest;
  token?: string | undefined;
  emitProgress?: (progress: TDownloadProgress) => void;
}): Promise<IRepositoryFileResponse | null> => {
  const resourcePath = getRepositoryFilePath(fileInfo);
  const requestHeaders = getRequestHeaders(token);
  try {
    const res = await apiClient.get(resourcePath, {
      headers: requestHeaders,
      onDownloadProgress: function (progressEvent) {
        var percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 0));
        emitProgress?.({ loaded: progressEvent.loaded, total: progressEvent.total || 0, percentCompleted });
      },
    });
    const resourceUrl = `${BASE_API_URL}${resourcePath}`;
    return responseHandler(res, fileInfo, resourceUrl);
  } catch (error: any) {
    Sentry.captureException(new Error('Failed to load repository file content'), {
      tags: {
        namespace: fileInfo.namespace,
        repository_name: fileInfo.repositoryName,
        resource_path: fileInfo.resource,
        datatype: fileInfo.dataType,
      },
    });
    return error.response.data;
  }
};
